// A team on the canvas (teams and matches, Ben 2026-10-30 — docs/modules/teams-and-matches.md).
// One module works for a player OR a team: a module showing a team keeps it as `_team` (its
// `_`-state, so Undo and saved dashboards carry it), and its player picker stays as it was
// underneath. Picking a player again clears `_team` (canvas/player-actions.ts).
//
// The pure half of the Bio's team card: league position, the record, form, the last result
// and the next game, from the two answers the League Table already uses (`/league-table`,
// `/matches`). No new server route.

import type { MatchSummary, ProtoLeagueStanding } from "@catbento/shared";

/** A team, as a module holds it. `season` = the season it was picked up in (a League Table's
 *  shown season), null = the league's newest. */
export interface TeamRef {
  id: number;
  name: string;
  competitionId: number;
  season: string | null;
}

/** The team a module shows (`_team`), or null when it shows a player. */
export function teamOfModule(mod: Element | null): TeamRef | null {
  if (!mod) return null;
  const v = (mod as unknown as { _team?: unknown })._team;
  if (v === undefined || v === null) return null;
  const t = v as Partial<TeamRef>;
  if (typeof t.id !== "number" || typeof t.name !== "string" || typeof t.competitionId !== "number") {
    throw new Error(`A module's _team isn't a team: ${JSON.stringify(v)}`);
  }
  return { id: t.id, name: t.name, competitionId: t.competitionId, season: typeof t.season === "string" ? t.season : null };
}

/** The team's place in the table, 1-based, in the League Table's own default order (points,
 *  highest first; ties keep the order the table came in). Null when it isn't in the table. */
export function tablePosition(table: readonly ProtoLeagueStanding[], teamId: number): number | null {
  const ordered = table.map((row, i) => ({ row, i })).sort((a, b) => b.row.points - a.row.points || a.i - b.i);
  const at = ordered.findIndex((x) => x.row.teamId === teamId);
  return at < 0 ? null : at + 1;
}

/** "1st", "2nd", "3rd", "11th", "22nd"… */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

export interface TeamGame {
  matchId: number;
  /** "YYYY-MM-DD". */
  date: string;
  kickOff: string | null;
  opponent: string;
  home: boolean;
  /** Played games only. */
  us: number | null;
  them: number | null;
}

/** The team's games in a season, oldest first: those played, and those still to come (no score
 *  yet, dated today or later). A past game with no score (postponed, or not filled in yet) is in
 *  neither. `today` = "YYYY-MM-DD". */
export function teamGames(matches: readonly MatchSummary[], teamId: number, today: string): { played: TeamGame[]; upcoming: TeamGame[] } {
  const played: TeamGame[] = [];
  const upcoming: TeamGame[] = [];
  const ordered = [...matches].sort((a, b) => (a.matchDate < b.matchDate ? -1 : a.matchDate > b.matchDate ? 1 : a.matchId - b.matchId));
  for (const m of ordered) {
    const home = m.homeTeamId === teamId;
    if (!home && m.awayTeamId !== teamId) continue;
    const date = m.matchDate.slice(0, 10);
    const game: TeamGame = {
      matchId: m.matchId,
      date,
      kickOff: m.kickOff,
      opponent: home ? m.awayTeamName : m.homeTeamName,
      home,
      us: null,
      them: null,
    };
    if (m.homeScore !== null && m.awayScore !== null) {
      played.push({ ...game, us: home ? m.homeScore : m.awayScore, them: home ? m.awayScore : m.homeScore });
    } else if (date >= today) {
      upcoming.push(game);
    }
  }
  return { played, upcoming };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "W", "D" or "L" for a played game. */
export function gameResult(g: TeamGame): "W" | "D" | "L" {
  if (g.us === null || g.them === null) throw new Error(`Match ${g.matchId} has no score`);
  return g.us > g.them ? "W" : g.us < g.them ? "L" : "D";
}

/** "W 2–1 vs Chelsea (H)" — a played game, as the League Table's form tooltip words it. */
export function resultLine(g: TeamGame): string {
  return `${gameResult(g)} ${g.us}–${g.them} vs ${g.opponent} (${g.home ? "H" : "A"})`;
}

/** "Sat 1 Nov" (and ", 15:00" when the kick-off is known). */
export function gameDate(g: Pick<TeamGame, "date" | "kickOff">): string {
  const d = new Date(`${g.date}T00:00:00Z`);
  const day = `${DAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  return g.kickOff ? `${day}, ${g.kickOff.slice(0, 5)}` : day;
}

/** "vs Liverpool (A)" — a game still to come. */
export function fixtureLine(g: TeamGame): string {
  return `vs ${g.opponent} (${g.home ? "H" : "A"})`;
}

/** "+26", "0", "−4". */
export function signed(n: number): string {
  return n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : "0";
}

/** Where a game was played, from the team's side. */
export type Venue = "home" | "away";

/** A record: "6W 2D 1L". */
function record(games: readonly TeamGame[]): string {
  const r = { W: 0, D: 0, L: 0 };
  for (const g of games) r[gameResult(g)] += 1;
  return `${r.W}W ${r.D}D ${r.L}L`;
}

/** The record overall, at home and away (the team Matches' chips). Played games only. */
export function venueCounts(games: readonly TeamGame[]): Record<"all" | Venue, { games: number; label: string }> {
  const home = games.filter((g) => g.home);
  const away = games.filter((g) => !g.home);
  return {
    all: { games: games.length, label: record(games) },
    home: { games: home.length, label: record(home) },
    away: { games: away.length, label: record(away) },
  };
}
